import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Play, Loader2, CheckCircle2, Search,
  Tag, ChevronDown, ChevronUp, Edit3, Save, X, Users, Shield
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const AUTH_COLORS = {
  binding_contract: "#f43f5e",
  binding_company_rule: "#f59e0b",
  procedural_requirement: "#3b82f6",
  guidance_reference: "#8b5cf6",
  training_only: "#6b7280",
};

const RISK_COLORS = {
  life_critical: "#f43f5e",
  high: "#f59e0b",
  moderate: "#3b82f6",
  low: "#6b7280",
};

const SCOPE_COLORS = {
  all_union_workers: "#22c55e",
  locator_only: "#f97316",
  clerical_only: "#a855f7",
  substation_only: "#3b82f6",
  role_specific: "#eab308",
  subgroup_only: "#ec4899",
};

const WORKER_SCOPES = [
  "all_union_workers", "locator_only", "clerical_only",
  "substation_only", "role_specific", "subgroup_only",
];

function StatCard({ label, value, sub, color }) {
  return (
    <div className="tr-stat" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="tr-stat-val" style={color ? { color } : {}}>{value}</div>
      <div className="tr-stat-label">{label}</div>
      {sub && <div className="tr-stat-sub">{sub}</div>}
    </div>
  );
}

function ScopeBar({ scopeData, total }) {
  if (!scopeData || total === 0) return null;
  return (
    <div className="tr-scope-bar" data-testid="scope-distribution">
      <div className="tr-scope-bar-label">Worker Scope Distribution</div>
      <div className="tr-scope-track">
        {Object.entries(scopeData).map(([scope, count]) => {
          const pct = (count / total) * 100;
          if (pct < 0.5) return null;
          return (
            <div
              key={scope}
              className="tr-scope-segment"
              style={{
                width: `${pct}%`,
                background: SCOPE_COLORS[scope] || "#6b7280",
              }}
              title={`${scope.replace(/_/g, " ")}: ${count} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div className="tr-scope-legend">
        {Object.entries(scopeData).map(([scope, count]) => (
          <span key={scope} className="tr-scope-legend-item">
            <span className="tr-scope-dot" style={{ background: SCOPE_COLORS[scope] || "#6b7280" }} />
            {scope.replace(/_/g, " ")} ({count})
          </span>
        ))}
      </div>
    </div>
  );
}

function ChunkRow({ chunk, onApprove, onScopeChange }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editScope, setEditScope] = useState(chunk.worker_scope || "all_union_workers");
  const [saving, setSaving] = useState(false);
  const authColor = AUTH_COLORS[chunk.authority_level] || "#6b7280";
  const riskColor = RISK_COLORS[chunk.risk_level] || "#6b7280";
  const scopeColor = SCOPE_COLORS[chunk.worker_scope] || "#6b7280";

  async function handleSaveScope() {
    setSaving(true);
    try {
      await axios.put(`${API}/api/tags/chunks/${chunk.chunk_id}`, {
        worker_scope: editScope,
        special_scope: editScope !== "all_union_workers",
        applies_to_all_union_workers: editScope === "all_union_workers",
      });
      onScopeChange(chunk.chunk_id, editScope);
      setEditing(false);
    } catch (e) {
      console.error("Failed to save scope:", e);
    }
    setSaving(false);
  }

  return (
    <div className="tr-chunk" data-testid={`chunk-${chunk.chunk_id}`}>
      <div className="tr-chunk-header" onClick={() => setExpanded(!expanded)}>
        <div className="tr-chunk-left">
          <span className="tr-chunk-id">{chunk.chunk_id}</span>
          <span className="tr-chunk-title">{chunk.title}</span>
        </div>
        <div className="tr-chunk-tags">
          <span className="tr-tag scope" style={{ borderColor: scopeColor, color: scopeColor }} data-testid={`scope-tag-${chunk.chunk_id}`}>
            <Users size={10} /> {chunk.worker_scope?.replace(/_/g, " ") || "unknown"}
          </span>
          <span className="tr-tag" style={{ borderColor: authColor, color: authColor }}>
            {chunk.authority_level?.replace(/_/g, " ")}
          </span>
          <span className="tr-tag" style={{ borderColor: riskColor, color: riskColor }}>
            {chunk.risk_level}
          </span>
          <span className="tr-tag topic">{chunk.topic}</span>
          <span className={`tr-status ${chunk.tag_status}`}>{chunk.tag_status}</span>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </div>
      {expanded && (
        <div className="tr-chunk-detail">
          <div className="tr-detail-grid">
            <DetailRow label="Source" value={chunk.source_document} />
            <DetailRow label="Section" value={chunk.source_section} />
            <DetailRow label="Topic" value={chunk.topic} />
            <DetailRow label="Subtopic" value={chunk.subtopic} />
            <DetailRow label="Confidence" value={`${Math.round((chunk.tag_confidence || 0) * 100)}%`} />
            <DetailRow label="Tag Status" value={chunk.tag_status} />
            <DetailRow label="Page Ref" value={chunk.page_reference} />
          </div>

          {/* Scope editing section */}
          <div className="tr-scope-edit-section" data-testid={`scope-edit-section-${chunk.chunk_id}`}>
            <div className="tr-scope-edit-header">
              <Shield size={14} />
              <span className="tr-detail-label">Worker Scope</span>
              {!editing && (
                <button className="tr-edit-btn" onClick={(e) => { e.stopPropagation(); setEditing(true); }} data-testid={`edit-scope-${chunk.chunk_id}`}>
                  <Edit3 size={12} /> Edit
                </button>
              )}
            </div>
            {editing ? (
              <div className="tr-scope-edit-controls" onClick={(e) => e.stopPropagation()}>
                <select
                  value={editScope}
                  onChange={(e) => setEditScope(e.target.value)}
                  className="tr-scope-select"
                  data-testid={`scope-select-${chunk.chunk_id}`}
                >
                  {WORKER_SCOPES.map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                  ))}
                </select>
                <button className="tr-save-btn" onClick={handleSaveScope} disabled={saving} data-testid={`save-scope-${chunk.chunk_id}`}>
                  {saving ? <Loader2 size={12} className="tr-spin" /> : <Save size={12} />} Save
                </button>
                <button className="tr-cancel-btn" onClick={() => { setEditing(false); setEditScope(chunk.worker_scope); }}>
                  <X size={12} /> Cancel
                </button>
              </div>
            ) : (
              <div className="tr-scope-display">
                <span className="tr-scope-badge" style={{ borderColor: scopeColor, color: scopeColor }}>
                  {chunk.worker_scope?.replace(/_/g, " ")}
                </span>
                {chunk.special_scope && <span className="tr-special-badge">Special Scope</span>}
                {chunk.applies_to_all_union_workers && <span className="tr-all-badge">All Workers</span>}
              </div>
            )}
          </div>

          {chunk.raw_chunk_text && (
            <div className="tr-chunk-text-preview">
              <span className="tr-detail-label">Chunk Text:</span>
              <pre>{chunk.raw_chunk_text.slice(0, 600)}{chunk.raw_chunk_text.length > 600 ? "..." : ""}</pre>
            </div>
          )}
          {chunk.keywords?.length > 0 && (
            <div className="tr-detail-list">
              <span className="tr-detail-label">Keywords:</span>
              {chunk.keywords.map((k, i) => <span key={i} className="tr-pill">{k}</span>)}
            </div>
          )}
          {chunk.intent?.length > 0 && (
            <div className="tr-detail-list">
              <span className="tr-detail-label">Intent:</span>
              {chunk.intent.map((k, i) => <span key={i} className="tr-pill intent">{k}</span>)}
            </div>
          )}
          {chunk.trigger_phrases?.length > 0 && (
            <div className="tr-detail-list">
              <span className="tr-detail-label">Triggers:</span>
              {chunk.trigger_phrases.map((k, i) => <span key={i} className="tr-pill trigger">{k}</span>)}
            </div>
          )}
          {chunk.worker_context?.length > 0 && (
            <div className="tr-detail-list">
              <span className="tr-detail-label">Worker ctx:</span>
              {chunk.worker_context.map((k, i) => <span key={i} className="tr-pill">{k}</span>)}
            </div>
          )}
          {chunk.equipment_context?.length > 0 && (
            <div className="tr-detail-list">
              <span className="tr-detail-label">Equipment:</span>
              {chunk.equipment_context.map((k, i) => <span key={i} className="tr-pill">{k}</span>)}
            </div>
          )}
          <div className="tr-chunk-actions">
            {chunk.tag_status !== "approved" && (
              <button className="tr-approve-btn" onClick={() => onApprove(chunk.chunk_id)} data-testid={`approve-${chunk.chunk_id}`}>
                <CheckCircle2 size={13} /> Approve
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="tr-detail-row">
      <span className="tr-detail-label">{label}</span>
      <span className="tr-detail-value">{value}</span>
    </div>
  );
}

export default function TagReviewPage({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [scopeStats, setScopeStats] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [taggerRunning, setTaggerRunning] = useState({});
  const [filters, setFilters] = useState({
    topic: "", source_type: "", tag_status: "", search: "", worker_scope: ""
  });
  const [page, setPage] = useState(0);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/tags/stats`);
      setStats(data);
    } catch (err) { console.warn("TagReview: loadStats failed", err); }
  }, []);

  const loadScopeStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/tags/scope-stats`);
      setScopeStats(data);
    } catch (err) { console.warn("TagReview: loadScopeStats failed", err); }
  }, []);

  const loadChunks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 30, skip: page * 30 };
      if (filters.topic) params.topic = filters.topic;
      if (filters.source_type) params.source_type = filters.source_type;
      if (filters.tag_status) params.tag_status = filters.tag_status;
      if (filters.search) params.search = filters.search;
      if (filters.worker_scope) params.worker_scope = filters.worker_scope;
      const { data } = await axios.get(`${API}/api/tags/chunks`, { params });
      setChunks(data.chunks);
      setTotal(data.total);
    } catch (err) { console.warn("TagReview: loadChunks failed", err); }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { loadStats(); loadScopeStats(); loadChunks(); }, [loadStats, loadScopeStats, loadChunks]);

  async function runTagger(phase) {
    setTaggerRunning((prev) => ({ ...prev, [phase]: true }));
    try {
      await axios.post(`${API}/api/tags/run-tagger/${phase}`);
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/api/tags/job-status/${phase}`);
          if (data.status === "completed" || data.status === "failed") {
            clearInterval(poll);
            setTaggerRunning((prev) => ({ ...prev, [phase]: false }));
            if (data.status === "failed") {
              alert(`Phase ${phase} failed: ${data.error?.substring(0, 100)}`);
            } else {
              alert(`Phase ${phase} complete: ${data.chunks_tagged} chunks tagged`);
            }
            loadStats();
            loadScopeStats();
            loadChunks();
          }
        } catch (err) {
          console.warn("TagReview: tagger poll failed", err);
          clearInterval(poll);
          setTaggerRunning((prev) => ({ ...prev, [phase]: false }));
        }
      }, 5000);
    } catch (err) {
      console.warn("TagReview: runTagger failed", err);
      setTaggerRunning((prev) => ({ ...prev, [phase]: false }));
    }
  }

  async function approveChunk(chunkId) {
    try {
      await axios.post(`${API}/api/tags/chunks/${chunkId}/approve`);
      setChunks((prev) => prev.map((c) => c.chunk_id === chunkId ? { ...c, tag_status: "approved" } : c));
      loadStats();
    } catch (err) { console.warn("TagReview: approveChunk failed", err); }
  }

  function handleScopeChange(chunkId, newScope) {
    setChunks((prev) => prev.map((c) =>
      c.chunk_id === chunkId ? { ...c, worker_scope: newScope, special_scope: newScope !== "all_union_workers", applies_to_all_union_workers: newScope === "all_union_workers" } : c
    ));
    loadScopeStats();
  }

  async function bulkApprove() {
    if (!window.confirm("Approve all auto-tagged chunks with 70%+ confidence?")) return;
    try {
      const { data } = await axios.post(`${API}/api/tags/bulk-approve?min_confidence=0.7`);
      alert(`Approved ${data.approved_count} chunks`);
      loadStats();
      loadChunks();
    } catch (err) { console.warn("TagReview: bulkApprove failed", err); }
  }

  return (
    <div className="tr-page" data-testid="tag-review-page">
      <style>{cssText}</style>

      <header className="tr-header">
        <div>
          <span className="tr-badge">Admin</span>
          <h1>Tag Review</h1>
          <p>Manage the AskStewie knowledge brain — auto-tag, review, approve, and fix worker scope.</p>
        </div>
      </header>

      {/* Stats Row */}
      {stats && (
        <div className="tr-stats-row" data-testid="tag-stats">
          <StatCard label="Unique Chunks" value={stats.unique_raw_chunks || stats.total_raw_chunks} sub={`${stats.total_raw_chunks} raw total`} />
          <StatCard label="Tagged" value={stats.total_tagged} sub={`${stats.coverage_pct}% coverage`} />
          <StatCard label="Avg Confidence" value={`${Math.round(stats.avg_confidence * 100)}%`} />
          <StatCard label="Auto" value={stats.by_status?.auto || 0} color="#f59e0b" />
          <StatCard label="Approved" value={stats.by_status?.approved || 0} color="#22c55e" />
        </div>
      )}

      {/* Scope Distribution Bar */}
      {scopeStats && (
        <ScopeBar scopeData={scopeStats.by_worker_scope} total={stats?.total_tagged || 0} />
      )}

      {/* Tagger controls */}
      <div className="tr-tagger-panel" data-testid="tagger-controls">
        <h2>Auto-Tagger Pipeline</h2>
        <div className="tr-tagger-row">
          {[1, 2, 3].map((phase) => (
            <button
              key={phase}
              className="tr-tagger-btn"
              disabled={taggerRunning[phase]}
              onClick={() => runTagger(phase)}
              data-testid={`run-phase-${phase}`}
            >
              {taggerRunning[phase] ? <Loader2 size={14} className="tr-spin" /> : <Play size={14} />}
              Phase {phase}: {phase === 1 ? "Contract + Safety" : phase === 2 ? "Equipment" : "Remaining"}
            </button>
          ))}
          <button className="tr-bulk-approve" onClick={bulkApprove} data-testid="bulk-approve">
            <CheckCircle2 size={14} /> Bulk Approve (70%+)
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tr-filters" data-testid="tag-filters">
        <div className="tr-search-wrap">
          <Search size={14} />
          <input
            placeholder="Search by title, keyword, or chunk ID..."
            value={filters.search}
            onChange={(e) => { setFilters((f) => ({ ...f, search: e.target.value })); setPage(0); }}
            data-testid="search-input"
          />
        </div>
        <select value={filters.worker_scope} onChange={(e) => { setFilters((f) => ({ ...f, worker_scope: e.target.value })); setPage(0); }} data-testid="scope-filter">
          <option value="">All scopes</option>
          {WORKER_SCOPES.map((s) => (
            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
          ))}
        </select>
        <select value={filters.tag_status} onChange={(e) => { setFilters((f) => ({ ...f, tag_status: e.target.value })); setPage(0); }} data-testid="status-filter">
          <option value="">All statuses</option>
          <option value="auto">Auto</option>
          <option value="reviewed">Reviewed</option>
          <option value="approved">Approved</option>
        </select>
        <select value={filters.topic} onChange={(e) => { setFilters((f) => ({ ...f, topic: e.target.value })); setPage(0); }} data-testid="topic-filter">
          <option value="">All topics</option>
          {stats?.by_topic && Object.keys(stats.by_topic).map((t) => (
            <option key={t} value={t}>{t} ({stats.by_topic[t]})</option>
          ))}
        </select>
      </div>

      {/* Chunk list */}
      <div className="tr-chunk-list" data-testid="chunk-list">
        {loading ? (
          <div className="tr-loading"><Loader2 size={20} className="tr-spin" /> Loading tagged chunks...</div>
        ) : chunks.length === 0 ? (
          <div className="tr-empty">
            <Tag size={32} />
            <p>No tagged chunks match your filters. Try adjusting your search or run the auto-tagger.</p>
          </div>
        ) : (
          <>
            <div className="tr-count">{total} tagged chunks</div>
            {chunks.map((c, idx) => (
              <ChunkRow key={`${c.chunk_id}-${idx}`} chunk={c} onApprove={approveChunk} onScopeChange={handleScopeChange} />
            ))}
            <div className="tr-pagination">
              {page > 0 && <button onClick={() => setPage(page - 1)} data-testid="prev-page">Previous</button>}
              <span>Page {page + 1} of {Math.ceil(total / 30)}</span>
              {(page + 1) * 30 < total && <button onClick={() => setPage(page + 1)} data-testid="next-page">Next</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const cssText = `
.tr-page { max-width: 1100px; margin: 0 auto; padding: 20px 16px; min-height: 100vh; }
.tr-header { margin-bottom: 20px; }
.tr-back { background: none; border: 1px solid var(--border-default); color: var(--text-secondary); padding: 6px 14px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-size: 13px; transition: all 0.15s; margin-bottom: 12px; }
.tr-back:hover { border-color: var(--border-hover); color: var(--text-primary); }
.tr-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: rgba(99,102,241,0.12); color: #818cf8; border: 1px solid rgba(99,102,241,0.2); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px; }
.tr-header h1 { font-size: 24px; font-weight: 700; margin: 4px 0; }
.tr-header p { color: var(--text-secondary); font-size: 14px; margin: 0; }

/* Stats */
.tr-stats-row { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
.tr-stat { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 110px; }
.tr-stat-val { font-size: 22px; font-weight: 700; color: var(--text-primary); }
.tr-stat-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.04em; }
.tr-stat-sub { font-size: 11px; color: var(--text-secondary); margin-top: 2px; }

/* Scope distribution bar */
.tr-scope-bar { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 14px; padding: 16px 18px; margin-bottom: 16px; }
.tr-scope-bar-label { font-size: 12px; font-weight: 700; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.04em; margin-bottom: 10px; }
.tr-scope-track { display: flex; height: 10px; border-radius: 99px; overflow: hidden; background: var(--surface-base); gap: 1px; }
.tr-scope-segment { height: 100%; border-radius: 2px; transition: width 0.3s; }
.tr-scope-legend { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 10px; }
.tr-scope-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; color: var(--text-secondary); }
.tr-scope-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

/* Tagger panel */
.tr-tagger-panel { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 14px; padding: 18px; margin-bottom: 16px; }
.tr-tagger-panel h2 { font-size: 15px; font-weight: 700; margin: 0 0 12px; }
.tr-tagger-row { display: flex; gap: 8px; flex-wrap: wrap; }
.tr-tagger-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.2); color: #818cf8; transition: all 0.15s; }
.tr-tagger-btn:hover { background: rgba(99,102,241,0.15); }
.tr-tagger-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.tr-bulk-approve { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.2); color: #22c55e; transition: all 0.15s; }
.tr-bulk-approve:hover { background: rgba(34,197,94,0.15); }

/* Filters */
.tr-filters { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.tr-search-wrap { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 200px; background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 10px; padding: 0 12px; }
.tr-search-wrap input { flex: 1; border: none; background: none; color: var(--text-primary); padding: 10px 0; font-size: 13px; outline: none; }
.tr-filters select { padding: 10px 12px; border-radius: 10px; background: var(--surface-card); border: 1px solid var(--border-default); color: var(--text-primary); font-size: 13px; cursor: pointer; }

/* Chunk list */
.tr-chunk-list { display: flex; flex-direction: column; gap: 4px; }
.tr-loading, .tr-empty { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 60px 20px; color: var(--text-muted); font-size: 14px; flex-direction: column; }
.tr-count { font-size: 12px; color: var(--text-muted); margin-bottom: 6px; }

/* Chunk row */
.tr-chunk { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 10px; overflow: hidden; }
.tr-chunk-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; cursor: pointer; gap: 10px; }
.tr-chunk-header:hover { background: rgba(255,255,255,0.02); }
.tr-chunk-left { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.tr-chunk-id { font-family: monospace; font-size: 11px; color: var(--text-muted); white-space: nowrap; }
.tr-chunk-title { font-size: 13px; font-weight: 600; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tr-chunk-tags { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.tr-tag { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 999px; border: 1px solid; text-transform: uppercase; letter-spacing: 0.03em; white-space: nowrap; }
.tr-tag.topic { border-color: rgba(34,211,238,0.3); color: #22d3ee; }
.tr-tag.scope { display: inline-flex; align-items: center; gap: 3px; }
.tr-status { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; }
.tr-status.auto { background: rgba(245,158,11,0.1); color: #f59e0b; }
.tr-status.reviewed { background: rgba(99,102,241,0.1); color: #818cf8; }
.tr-status.approved { background: rgba(34,197,94,0.1); color: #22c55e; }

/* Chunk detail */
.tr-chunk-detail { padding: 0 14px 14px; border-top: 1px solid var(--border-default); }
.tr-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 16px; padding: 10px 0; }
.tr-detail-row { display: flex; gap: 8px; }
.tr-detail-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; min-width: 80px; }
.tr-detail-value { font-size: 13px; color: var(--text-secondary); }
.tr-detail-list { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; margin-top: 6px; }
.tr-chunk-text-preview { margin-top: 8px; }
.tr-chunk-text-preview pre { background: var(--surface-base); border: 1px solid var(--border-default); border-radius: 8px; padding: 10px 12px; font-size: 12px; line-height: 1.5; color: var(--text-secondary); white-space: pre-wrap; word-break: break-word; max-height: 200px; overflow-y: auto; margin-top: 4px; }
.tr-pill { font-size: 11px; padding: 2px 8px; border-radius: 6px; background: var(--surface-base); border: 1px solid var(--border-default); color: var(--text-secondary); }
.tr-pill.intent { border-color: rgba(99,102,241,0.25); color: #818cf8; }
.tr-pill.trigger { border-color: rgba(245,158,11,0.25); color: #fbbf24; }

/* Scope editing */
.tr-scope-edit-section { background: var(--surface-base); border: 1px solid var(--border-default); border-radius: 10px; padding: 12px; margin-top: 10px; }
.tr-scope-edit-header { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
.tr-edit-btn { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; background: rgba(99,102,241,0.08); border: 1px solid rgba(99,102,241,0.2); color: #818cf8; margin-left: auto; }
.tr-edit-btn:hover { background: rgba(99,102,241,0.15); }
.tr-scope-edit-controls { display: flex; align-items: center; gap: 6px; }
.tr-scope-select { padding: 6px 10px; border-radius: 8px; background: var(--surface-card); border: 1px solid var(--border-default); color: var(--text-primary); font-size: 12px; cursor: pointer; flex: 1; }
.tr-save-btn { display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: #22c55e; }
.tr-save-btn:hover { background: rgba(34,197,94,0.2); }
.tr-save-btn:disabled { opacity: 0.5; }
.tr-cancel-btn { display: inline-flex; align-items: center; gap: 4px; padding: 5px 12px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); color: #ef4444; }
.tr-cancel-btn:hover { background: rgba(239,68,68,0.15); }
.tr-scope-display { display: flex; align-items: center; gap: 8px; }
.tr-scope-badge { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 6px; border: 1px solid; }
.tr-special-badge { font-size: 10px; padding: 2px 8px; border-radius: 999px; background: rgba(245,158,11,0.1); color: #f59e0b; border: 1px solid rgba(245,158,11,0.2); }
.tr-all-badge { font-size: 10px; padding: 2px 8px; border-radius: 999px; background: rgba(34,197,94,0.1); color: #22c55e; border: 1px solid rgba(34,197,94,0.2); }

.tr-chunk-actions { display: flex; gap: 8px; margin-top: 10px; }
.tr-approve-btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.2); color: #22c55e; transition: all 0.15s; }
.tr-approve-btn:hover { background: rgba(34,197,94,0.15); }

/* Pagination */
.tr-pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 14px 0; }
.tr-pagination button { padding: 6px 16px; border-radius: 8px; border: 1px solid var(--border-default); background: var(--surface-card); color: var(--text-secondary); font-size: 13px; cursor: pointer; }
.tr-pagination button:hover { border-color: var(--border-hover); }
.tr-pagination span { font-size: 12px; color: var(--text-muted); }

.tr-spin { animation: trSpin 1s linear infinite; }
@keyframes trSpin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .tr-stats-row { flex-direction: column; }
  .tr-tagger-row { flex-direction: column; }
  .tr-filters { flex-direction: column; }
  .tr-chunk-header { flex-direction: column; align-items: flex-start; }
  .tr-chunk-tags { flex-wrap: wrap; }
  .tr-detail-grid { grid-template-columns: 1fr; }
  .tr-scope-edit-controls { flex-direction: column; }
}
`;
